import type { LogLine } from './sse';
import TITOLI from '../../../../internal/jobs/titoli.json';
import { localeIntl, t } from './lingua';

/**
 * Le parole con cui il pannello racconta un'operazione lunga.
 *
 * L'hub parla per script: `provision_server`, `step 14/21 — firewall`. Sono
 * nomi giusti per chi legge un log e sbagliati per chi aspetta che il proprio
 * server sia pronto. Qui c'è la traduzione, in un posto solo: il tipo di job
 * diventa un titolo, ogni step un passo con parole sue, e dalle righe del log
 * si ricava l'elenco dei passi con il loro stato.
 *
 * Le sequenze sono ricopiate dalle cartelle degli script in Go
 * (`internal/provision/scripts`, `internal/instances/scripts`). Non c'è una
 * fonte da cui derivarle a runtime — il log dice solo il passo corrente e il
 * totale — e se una sequenza cambia senza aggiornare questo file il pannello
 * non mente: il passo arrivato nel log vince sempre su quello previsto qui,
 * e un totale diverso da quello previsto fa cadere l'elenco anticipato.
 */

/**
 * Il titolo dell'operazione, da completare con il nome del bersaglio.
 *
 * La tabella sta in `internal/jobs/titoli.json` e non qui perché la legge anche
 * l'hub: l'assistente (08-mcp §7) racconta i job con le stesse parole del
 * pannello, e due copie della stessa tabella prima o poi dicono cose diverse.
 * Un tipo di job nuovo va aggiunto lì; `esitoOk` qui sotto non compila finché
 * non ha la sua frase.
 */
export type KindJob = keyof typeof TITOLI;
export const TITOLO: Record<KindJob, string> = TITOLI;

/**
 * Cosa succede quando finisce bene: la frase del banner di chiusura. Una
 * funzione e non una tabella, perché la frase si dice nella lingua di chi
 * guarda quando guarda; la tabella dentro resta completa per ogni tipo.
 */
export function esitoOk(kind: string): string | undefined {
  const frasi: Record<KindJob, string> = {
    provision_server: t('Il server è pronto: ci puoi creare un’istanza.'),
    decommission_server: t('Il server non è più gestito da Molo: le sue istanze non compaiono più e i loro indirizzi sono stati tolti.'),
    create_instance: t('L’istanza è attiva.'),
    delete_instance: t('L’istanza non c’è più.'),
    restore_instance: t('La copia è attiva, accanto all’originale.'),
    backup_instance: t('Il backup è al sicuro nella destinazione.'),
    start_instance: t('L’istanza è attiva e risponde.'),
    stop_instance: t('L’istanza è ferma. Resta ferma anche se il server si riavvia.'),
    restart_instance: t('L’istanza è di nuovo attiva e risponde.'),
    configure_instance: t('La nuova configurazione è attiva: Odoo è ripartito e risponde.'),
    update_instance: t('Odoo è aggiornato e l’istanza risponde.'),
    create_staging: t('La copia di prova è pronta e risponde.'),
    refresh_staging: t('La copia ha di nuovo i dati e il codice della produzione.'),
    restore_in_place: t('I dati sono quelli del backup: Odoo è ripartito e risponde. Quelli di prima restano nel backup di sicurezza.'),
    test_restore: t('Il backup si ricarica: utenti, moduli e allegati ci sono tutti.'),
    set_domain: t('Il dominio risponde in HTTPS con il suo certificato.'),
    remove_domain: t('Il dominio non è più collegato.'),
    dr_run: t('La corsa è finita: la scheda dei tempi è in Continuità.'),
    connect_source: t('Molo è entrato nella macchina e ne ha letto le istanze: le trovi in Traslochi.'),
    inventory_source: t('L’inventario è aggiornato: le istanze sono in Traslochi.'),
    disconnect_source: t('Sulla macchina non c’è più niente di Molo.'),
    migration_trial: t('La prova è pronta: guardala, e il giorno del trasloco premi «Trasloca».'),
    migration_cutover: t('L’istanza è su Molo e CloudPepper è spento: adesso gli indirizzi.'),
    migration_revert: t('L’originale su CloudPepper è di nuovo acceso, e il nome punta di nuovo lì.'),
    import_instance: t('L’istanza è attiva con i dati di CloudPepper.'),
    backup_retention: t('I backup vecchi sono stati eliminati.'),
    backup_verify: t('Il backup si rilegge correttamente.'),
    backup_scheduler: t('I backup pianificati sono stati accodati.'),
    backup_servers: t('I certificati di ogni server sono al sicuro nel backup.'),
    restore_test_scheduler: t('Le prove di ripristino del giorno sono state accodate.'),
    metrics_retention: t('Le metriche vecchie sono state eliminate.'),
    ping: t('Tutto risponde.'),
  };
  return (frasi as Record<string, string>)[kind];
}

export function titolo(kind: string, bersaglio?: string): string {
  const it = (TITOLO as Record<string, string>)[kind];
  const base = it ? t(it) : kind;
  return bersaglio ? `${base} ${bersaglio}` : base;
}

/**
 * I passi, nell'ordine degli script: chiave (il nome nel log) e parole di chi
 * guarda. Funzioni e non tabelle, come `esitoOk`: le parole si dicono nella
 * lingua di chi guarda.
 */
const passiServer = (): Array<[string, string]> => [
  ['dist-upgrade', t('Aggiornamento del sistema')],
  ['locale-time', t('Lingua e orologio')],
  ['swap', t('Memoria di riserva')],
  ['sysctl', t('Regolazione del sistema')],
  ['journald', t('Registro di sistema')],
  ['unattended-upgrades', t('Aggiornamenti di sicurezza automatici')],
  ['packages', t('Programmi di base')],
  ['fonts', t('Caratteri per i PDF')],
  ['node-rtlcss', t('Strumenti per l’interfaccia web')],
  ['wkhtmltopdf', t('Generatore di PDF')],
  ['postgresql', t('Database PostgreSQL')],
  ['postgresql-tuning', t('Regolazione del database')],
  ['nginx', t('Server web')],
  ['acme', t('Strumento per i certificati')],
  ['firewall', t('Firewall')],
  ['fail2ban', t('Protezione dagli accessi indesiderati')],
  ['sshd', t('Accesso sicuro')],
  ['odoo-base', t('Base comune di Odoo')],
  ['agent', t('Agente di monitoraggio')],
  ['verify', t('Verifica finale')],
  ['restic', t('Strumento di backup')],
  ['tls', t('Certificato HTTPS')],
  ['initial-access', t('Chiusura dell’accesso iniziale')],
  ['ip-fisso', t('IP fisso')],
];

const passiIstanza = (): Array<[string, string]> => [
  ['tree', t('Cartelle dell’istanza')],
  ['source', t('Codice di Odoo')],
  ['venv', t('Ambiente Python')],
  ['addons', t('Moduli aggiuntivi')],
  ['database', t('Database')],
  ['config', t('Configurazione')],
  ['init', t('Primo avvio di Odoo')],
  ['unit', t('Servizio di sistema')],
  ['nginx', t('Indirizzo web')],
  ['smoke', t('Prova di funzionamento')],
];

/** Come la creazione, con il primo avvio di Odoo al posto del passo che porta i dati. */
const conDati = (chiave: string, nome: string): Array<[string, string]> =>
  passiIstanza().map(([k, n]) => (k === 'init' ? [chiave, nome] : [k, n]));

const passiRimozione = (): Array<[string, string]> => [['remove', t('Rimozione dell’istanza')]];

/** La riconfigurazione: gli step della creazione che scrivono file, un riavvio, la prova. */
const passiRiconfigurazione = (): Array<[string, string]> => [
  ['addons', t('Moduli aggiuntivi')],
  ['config', t('Configurazione')],
  ['unit', t('Servizio di sistema')],
  ['restart', t('Riavvio con la nuova configurazione')],
  ['nginx', t('Indirizzo web')],
  ['smoke', t('Prova di funzionamento')],
];

/** Il ripristino in produzione: prima si mette al sicuro ciò che c'è, poi si sostituisce. */
const passiRipristinoProduzione = (): Array<[string, string]> => [
  ['backup', t('Backup di sicurezza dei dati attuali')],
  ['fetch', t('Recupero dello snapshot')],
  ['restore', t('Sostituzione dei dati e riavvio')],
];

/** La prova di ripristino: lo snapshot si ricarica in un database a parte e si butta. */
const passiProva = (): Array<[string, string]> => [
  ['fetch', t('Recupero dello snapshot')],
  ['verify', t('Carico in un database a parte e controlli')],
];

function sequenze(): Record<string, Array<[string, string]> | undefined> {
  const s: Partial<Record<KindJob, Array<[string, string]>>> = {
    provision_server: passiServer(),
    create_instance: passiIstanza(),
    restore_instance: conDati('restore', t('Ripristino dei dati')),
    delete_instance: passiRimozione(),
    configure_instance: passiRiconfigurazione(),
    create_staging: conDati('duplica', t('Copia dei dati')),
    import_instance: conDati('importa', t('Import dei dati')),
    refresh_staging: [
      ['source', t('Codice di Odoo')],
      ['venv', t('Ambiente Python')],
      ['addons', t('Moduli aggiuntivi')],
      ['config', t('Configurazione')],
      ['duplica', t('Copia dei dati')],
      ['restart', t('Riavvio')],
    ],
    restore_in_place: passiRipristinoProduzione(),
    test_restore: passiProva(),
  };
  return s;
}

/** I nomi di tutti i passi per chiave, per uno step fuori da ogni sequenza nota. */
function nomi(): Record<string, string> {
  return Object.fromEntries([
    ...passiServer(), ...passiIstanza(), ...conDati('restore', t('Ripristino dei dati')), ...passiRimozione(),
    ...passiRiconfigurazione(), ...passiRipristinoProduzione(), ...passiProva(),
  ]);
}

/** Il nome umano dell'n-esimo passo del provisioning, per chi ha solo il numero. */
export function nomePassoServer(n: number, totale: number): string {
  const seq = passiServer();
  const passo = totale === seq.length ? seq[n - 1]?.[1] : undefined;
  return passo ?? t('Passo {n} di {totale}', { n, totale });
}

const NOME_PROVIDER: Record<string, string> = { ovh: 'OVH' };

/**
 * A che punto è un server prima dei passi, detto dal lato della macchina.
 * Gli stati dell'hub seguono quelli del provider: `waiting_ip` è la macchina
 * che il provider sta ancora preparando (`BUILD` su OVH), `waiting_ssh` quella
 * accesa che non risponde ancora.
 */
export function faseContatto(status: string, provider: string, ipInArrivo = false): string {
  const chi = NOME_PROVIDER[provider] ?? t('il provider');
  const Chi = `${chi.charAt(0).toUpperCase()}${chi.slice(1)}`;
  // L'IP fisso comprato si aspetta con la macchina già accesa, prima dei passi.
  if (ipInArrivo && status === 'waiting_ssh') return t('{chi} sta consegnando l’IP fisso', { chi: Chi });
  switch (status) {
    case 'creating': return t('Chiedo la macchina a {chi}', { chi });
    case 'waiting_ip': return t('{chi} sta preparando la macchina', { chi: Chi });
    case 'waiting_ssh': return t('La macchina è accesa, aspetto che risponda');
    case 'verifying': return t('Verifica finale');
    default: return t('Sto raggiungendo la macchina');
  }
}

/**
 * Il nome umano di uno step, o il suo nome nel log se non lo conosciamo.
 *
 * Prima si guarda nella sequenza del tipo di job: `nginx` è «Server web» in
 * un'installazione e «Indirizzo web» in un'istanza, e la mappa comune non
 * saprebbe quale dei due.
 */
export function nomePasso(chiave: string, kind?: string): string {
  const seq = kind ? sequenze()[kind] : undefined;
  return seq?.find(([k]) => k === chiave)?.[1] ?? nomi()[chiave] ?? chiave;
}

export interface Passo {
  n: number;
  chiave: string;
  title: string;
  detail?: string;
  state: 'done' | 'current' | 'pending' | 'failed';
}

const RIGA_STEP = /^step (\d+)\/(\d+) — (.+)$/;

/**
 * L'elenco dei passi, dallo stato del job e dalle righe del log.
 *
 * Le righe dell'hub dicono dove si è arrivati; la sequenza prevista dice cosa
 * manca. Se il totale nel log non è quello previsto, la sequenza prevista è
 * vecchia e si mostrano solo i passi visti davvero.
 */
export function passi(
  kind: string,
  lines: LogLine[],
  stato: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | undefined,
): Passo[] {
  const visti = new Map<number, { chiave: string; saltato: boolean }>();
  let totale = 0;
  let ultimo = 0;
  for (const l of lines) {
    const m = l.stream === 'h' ? l.line.match(RIGA_STEP) : null;
    if (m) {
      const n = Number(m[1]);
      totale = Number(m[2]);
      ultimo = n;
      visti.set(n, { chiave: m[3]!, saltato: false });
    } else if (ultimo > 0 && /^>>> già fatto, salto/.test(l.line)) {
      visti.get(ultimo)!.saltato = true;
    }
  }

  const prevista = sequenze()[kind];
  const usaPrevista = prevista !== undefined && (totale === 0 || totale === prevista.length);
  const n = usaPrevista ? prevista!.length : totale;
  if (n === 0) return [];

  const finito = stato === 'succeeded';
  const fallito = stato === 'failed' || stato === 'cancelled';
  const out: Passo[] = [];
  for (let i = 1; i <= n; i += 1) {
    const visto = visti.get(i);
    const chiave = visto?.chiave ?? (usaPrevista ? prevista![i - 1]![0] : `${i}`);
    const title = visto || usaPrevista ? nomePasso(chiave, kind) : t('Passo {n}', { n: i });
    let state: Passo['state'];
    if (finito || i < ultimo) state = 'done';
    else if (i === ultimo) state = fallito ? 'failed' : stato === 'running' || stato === 'queued' ? 'current' : 'done';
    else state = 'pending';
    out.push({
      n: i,
      chiave,
      title,
      state,
      detail: visto?.saltato ? t('Era già fatto') : undefined,
    });
  }
  return out;
}

/** Dove si è arrivati, per una barra: passo e totale. Zero se il log non lo dice. */
export function avanzamento(lines: LogLine[]): { step: number; total: number; chiave?: string } {
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const m = lines[i]!.line.match(RIGA_STEP);
    if (m) return { step: Number(m[1]), total: Number(m[2]), chiave: m[3] };
  }
  return { step: 0, total: 0 };
}

/** Un momento nel tempo, come lo direbbe una persona: «2 minuti fa», «ieri alle 14:30». */
export function quandoRelativo(iso: string | undefined, adesso = Date.now()): string {
  if (!iso) return '—';
  const secondi = Math.round((adesso - new Date(iso).getTime()) / 1000);
  if (secondi < 45) return t('adesso');
  if (secondi < 90) return t('un minuto fa');
  const minuti = Math.round(secondi / 60);
  if (minuti < 60) return t('{n} minuti fa', { n: minuti });
  const ore = Math.round(minuti / 60);
  if (ore < 24) return ore === 1 ? t('un’ora fa') : t('{n} ore fa', { n: ore });
  const giorni = Math.round(ore / 24);
  if (giorni === 1) return t('ieri');
  if (giorni < 7) return t('{n} giorni fa', { n: giorni });
  return new Date(iso).toLocaleDateString(localeIntl(), { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Una durata, dai secondi: «3 min», «1 h 20 min», «12 giorni». */
export function durata(secondi: number): string {
  if (secondi < 60) return `${Math.max(0, Math.round(secondi))} s`;
  const minuti = Math.round(secondi / 60);
  if (minuti < 60) return `${minuti} min`;
  const ore = Math.floor(minuti / 60);
  if (ore < 24) return minuti % 60 ? `${ore} h ${minuti % 60} min` : `${ore} h`;
  const giorni = Math.floor(ore / 24);
  return giorni === 1 ? t('1 giorno') : t('{n} giorni', { n: giorni });
}
